import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Filter, MessageSquare } from "lucide-react";
import { Column, DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RatingStars } from "@/components/pages/requests/components/activity/RatingStars";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import { SERVICES_CHART_CARD_ICON_COLOR } from "../services/constants";
import {
  REVIEWS_PAGE_SIZE,
  REVIEWS_RELATED_TABLE_BODY_CELL_CLASS,
  REVIEWS_RELATED_TABLE_HEADER_CELL_CLASS,
} from "./constants";
import { cn } from "@/lib/utils";
import type { ReviewTableRow } from "./types";
import { ANALYTICS_TOOLBAR_BUTTON_NEUTRAL_CLASS } from "../shared/toolbarButtonStyles";
import { AnalyticsExportMenu } from "../shared/AnalyticsExportMenu";
import { downloadReviewsRelatedReviewsExport } from "./reviewsTabExport";
import type { ReviewAnalyticsTabApi } from "@/types/analytics-reviews";

const REVIEWS_TABLE_ACTION_BUTTON_CLASS =
  ANALYTICS_TOOLBAR_BUTTON_NEUTRAL_CLASS;
const RATING_FILTER_OPTIONS = [5, 4, 3, 2, 1] as const;

function parseFeedbackDate(value: string): { date: string; time: string } {
  const [date = value, time = ""] = value
    .split(", ")
    .map((part) => part.trim());
  return { date, time };
}

type SortDirection = "asc" | "desc" | null;
type ReviewsSortField = "guestName" | "feedbackDate" | "feedback" | "rating";

type ReviewsSortState = {
  field: ReviewsSortField | null;
  direction: SortDirection;
};

const INITIAL_SORT: ReviewsSortState = { field: null, direction: null };
const SORTABLE_REVIEW_FIELDS = new Set<ReviewsSortField>([
  "guestName",
  "feedbackDate",
  "feedback",
  "rating",
]);
const REVIEW_TEXT_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

function isReviewsSortField(field: string): field is ReviewsSortField {
  return SORTABLE_REVIEW_FIELDS.has(field as ReviewsSortField);
}

/**
 * The feedback date is a day-first display string ("DD/MM/YYYY, HH:mm" for real
 * data, "DD.MM.YYYY, HH:mm" for previews), so it must be parsed into a real
 * timestamp before comparing — a lexical sort would order it incorrectly.
 */
function feedbackDateSortValue(value: string): number {
  const match = value.match(
    /(\d{1,2})[./](\d{1,2})[./](\d{4})(?:,?\s+(\d{1,2}):(\d{2}))?/,
  );
  if (!match) {
    return 0;
  }
  const [, day, month, year, hour = "0", minute = "0"] = match;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  ).getTime();
}

function compareReviewRows(
  a: ReviewTableRow,
  b: ReviewTableRow,
  field: ReviewsSortField,
): number {
  switch (field) {
    case "guestName":
      return REVIEW_TEXT_COLLATOR.compare(a.guestName, b.guestName);
    case "feedback":
      return REVIEW_TEXT_COLLATOR.compare(a.feedback, b.feedback);
    case "rating":
      return a.rating - b.rating;
    case "feedbackDate":
      return (
        feedbackDateSortValue(a.feedbackDate) -
        feedbackDateSortValue(b.feedbackDate)
      );
    default:
      return 0;
  }
}

function matchesReviewSearch(row: ReviewTableRow, search: string): boolean {
  if (!search) {
    return true;
  }

  const haystack = [
    row.guestName,
    row.countryCode,
    row.feedbackDate,
    row.feedback,
  ]
    .join(" ")
    .toLowerCase();

  return haystack.includes(search);
}

interface ReviewsRelatedTableProps {
  reviews: ReviewTableRow[];
  hotelId: string;
  from: string;
  to: string;
  tab: ReviewAnalyticsTabApi;
}

export const ReviewsRelatedTable: React.FC<ReviewsRelatedTableProps> = ({
  reviews,
  hotelId,
  from,
  to,
  tab,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState<ReviewsSortState>(INITIAL_SORT);
  const [search, setSearch] = useState("");
  const [selectedRatings, setSelectedRatings] = useState<number[]>([]);

  const normalizedSearch = search.trim().toLowerCase();
  const hasActiveFilters =
    normalizedSearch.length > 0 || selectedRatings.length > 0;
  const activeFilterCount =
    (normalizedSearch.length > 0 ? 1 : 0) + selectedRatings.length;

  const filteredReviews = useMemo(
    () =>
      reviews.filter(
        (row) =>
          matchesReviewSearch(row, normalizedSearch) &&
          (selectedRatings.length === 0 ||
            selectedRatings.includes(Math.round(row.rating))),
      ),
    [normalizedSearch, reviews, selectedRatings],
  );

  const sortedReviews = useMemo(() => {
    if (!sort.field || !sort.direction) {
      return filteredReviews;
    }
    const sortField = sort.field;
    const factor = sort.direction === "asc" ? 1 : -1;

    return [...filteredReviews].sort((a, b) => {
      const comparison = compareReviewRows(a, b, sortField);

      if (comparison !== 0) {
        return comparison * factor;
      }

      return a.id.localeCompare(b.id);
    });
  }, [filteredReviews, sort.field, sort.direction]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedReviews.length / REVIEWS_PAGE_SIZE),
  );
  const safePage = Math.min(currentPage, totalPages);

  const paginatedReviews = useMemo(() => {
    const start = (safePage - 1) * REVIEWS_PAGE_SIZE;
    return sortedReviews.slice(start, start + REVIEWS_PAGE_SIZE);
  }, [sortedReviews, safePage]);

  const handleSort = (field: string) => {
    if (!isReviewsSortField(field)) {
      return;
    }

    setCurrentPage(1);
    setSort((previous) => {
      if (previous.field !== field) {
        return { field, direction: "asc" };
      }
      if (previous.direction === "asc") {
        return { field, direction: "desc" };
      }
      if (previous.direction === "desc") {
        return INITIAL_SORT;
      }
      return { field, direction: "asc" };
    });
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleRatingToggle = (rating: number, checked: boolean) => {
    setSelectedRatings((previous) => {
      const next = checked
        ? [...previous, rating]
        : previous.filter((item) => item !== rating);
      return next.sort((a, b) => b - a);
    });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setSelectedRatings([]);
    setCurrentPage(1);
  };

  const handleExport = useCallback(
    (format: "csv" | "xlsx") => {
      if (sortedReviews.length === 0) {
        return;
      }

      downloadReviewsRelatedReviewsExport({
        hotelId,
        from,
        to,
        tab,
        format,
        reviews: sortedReviews,
      });
    },
    [from, hotelId, sortedReviews, tab, to],
  );

  const columns: Column<ReviewTableRow>[] = useMemo(
    () => [
      {
        key: "guestName",
        header: t("analytics.common.name", "Name"),
        headerClassName: REVIEWS_RELATED_TABLE_HEADER_CELL_CLASS,
        sortable: true,
        className: cn(
          REVIEWS_RELATED_TABLE_BODY_CELL_CLASS,
          "h-[72px] max-h-[72px] min-w-[160px]",
        ),
        render: (_, row) => <span>{row.guestName}</span>,
      },
      {
        key: "feedbackDate",
        header: t("analytics.reviews.related.feedbackDate", "Feedback Date"),
        headerClassName: REVIEWS_RELATED_TABLE_HEADER_CELL_CLASS,
        sortable: true,
        className: cn(
          REVIEWS_RELATED_TABLE_BODY_CELL_CLASS,
          "h-[72px] max-h-[72px] text-[#737373] text-xs",
        ),
        render: (value) => {
          const { date, time } = parseFeedbackDate(String(value));
          return (
            <div className="flex flex-col items-center gap-0.5 leading-none">
              <span>{date}</span>
              <span>{time}</span>
            </div>
          );
        },
      },
      {
        key: "feedback",
        header: t("analytics.services.detail.reviews.feedback", "Feedback"),
        headerClassName: REVIEWS_RELATED_TABLE_HEADER_CELL_CLASS,
        className: cn(
          REVIEWS_RELATED_TABLE_BODY_CELL_CLASS,
          "h-[72px] max-h-[72px] w-[295px] max-w-[295px] whitespace-normal",
        ),
        render: (value) => (
          <div className="flex w-full items-center justify-center">
            <span className="line-clamp-2 block w-full text-[10px] leading-none font-normal text-[#737373]">
              &ldquo;{value}&rdquo;
            </span>
          </div>
        ),
      },
      {
        key: "rating",
        header: t("analytics.reviews.related.evaluations", "Evaluations"),
        headerClassName: REVIEWS_RELATED_TABLE_HEADER_CELL_CLASS,
        sortable: true,
        className: cn(
          REVIEWS_RELATED_TABLE_BODY_CELL_CLASS,
          "h-[72px] max-h-[72px]",
        ),
        render: (_, row) => (
          <RatingStars
            value={row.rating}
            readOnly
            size={14}
            className="justify-center"
          />
        ),
      },
    ],
    [t],
  );

  return (
    <RevenueChartCard
      title={t("analytics.reviews.related.title", "Related Reviews")}
      icon={<MessageSquare />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      className="w-full"
      showExport={false}
    >
      <div className="mb-4 flex items-center justify-end gap-2">
        <Popover>
          <PopoverTrigger asChild disabled={reviews.length === 0}>
            <Button
              type="button"
              variant="span"
              className={REVIEWS_TABLE_ACTION_BUTTON_CLASS}
            >
              <Filter className="size-3.5 shrink-0" />
              {hasActiveFilters
                ? t(
                    "analytics.common.filtersWithCount",
                    "Filters ({{count}})",
                    {
                      count: activeFilterCount,
                    },
                  )
                : t("analytics.common.filters", "Filters")}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[280px] p-3">
            <div className="space-y-3">
              <Input
                value={search}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder={t(
                  "analytics.reviews.related.searchPlaceholder",
                  "Search reviews",
                )}
                className="h-9"
              />

              <div className="space-y-2">
                <p className="text-xs font-medium text-[#737373]">
                  {t("analytics.reviews.related.ratingFilter", "Rating")}
                </p>
                <div className="grid grid-cols-5 gap-2">
                  {RATING_FILTER_OPTIONS.map((rating) => {
                    const checked = selectedRatings.includes(rating);

                    return (
                      <label
                        key={rating}
                        className="flex cursor-pointer items-center gap-1.5 text-xs text-[#171717]"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) =>
                            handleRatingToggle(rating, value === true)
                          }
                        />
                        {rating}
                      </label>
                    );
                  })}
                </div>
              </div>

              <Button
                type="button"
                variant="span"
                disabled={!hasActiveFilters}
                onClick={handleClearFilters}
                className={cn(
                  REVIEWS_TABLE_ACTION_BUTTON_CLASS,
                  "w-full justify-center",
                )}
              >
                {t("analytics.common.clearFilters", "Clear filters")}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <AnalyticsExportMenu
          onExport={handleExport}
          disabled={sortedReviews.length === 0}
          className={REVIEWS_TABLE_ACTION_BUTTON_CLASS}
        />
      </div>

      <DataTable
        data={paginatedReviews}
        columns={columns}
        tableClassName="table-fixed w-full"
        getRowKey={(row) => row.id}
        columnHeaderClassName="text-[10px] font-medium text-[#737373]"
        emptyMessage={
          hasActiveFilters
            ? t(
                "analytics.reviews.related.noFilterResults",
                "No reviews match these filters.",
              )
            : t("analytics.reviews.related.empty", "No reviews yet.")
        }
        sorting={{
          field: sort.field,
          direction: sort.direction,
          onSort: handleSort,
        }}
        withPagination={totalPages > 1}
        pagination={
          totalPages > 1
            ? {
                currentPage: safePage,
                totalPages,
                onPageChange: setCurrentPage,
              }
            : undefined
        }
      />
    </RevenueChartCard>
  );
};
